import { Alert, Box, Chip, List, ListItem, ListItemButton, ListItemText, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import moment from 'moment';
import TimeUtils from '../../../utils/TimeUtils';
import { AccountData, OrderData } from '../../../api';
import { getTimeFormat } from '../../../common/getDateAndTimeFormat';
import { orderMatchesSearch } from '../../CalendarUtils';

type SearchFallbackViewProps = {
  orders: OrderData[];
  account: AccountData | null;
  permissionDenied: boolean;
  onRowTap: (order: OrderData) => void;
};

const SearchFallbackView = ({ orders, account, permissionDenied, onRowTap }: SearchFallbackViewProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 150);
  const timeFormat = getTimeFormat(account || undefined);

  const filtered = useMemo(() => {
    if (!debouncedQuery.trim()) return orders;
    return orders.filter((o) => orderMatchesSearch(o, debouncedQuery));
  }, [orders, debouncedQuery]);

  return (
    <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {permissionDenied && (
        <Alert severity="warning" sx={{ mx: 3, mt: 2 }}>
          {t('checkin_scanner.permission_denied_banner')}
        </Alert>
      )}
      <Box sx={{ px: 3, py: 2 }}>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('checkin_scanner.search_placeholder')}
          autoFocus
        />
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            {t('checkin_scanner.search_empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {filtered.map((order) => {
              const name = `${order.firstname} ${order.lastname}`.trim();
              const { checkin } = order;
              const timeLabel = order.time ? moment.unix(order.time).tz('utc').format(timeFormat) : '';
              return (
                <ListItem key={order.id} disablePadding divider>
                  <ListItemButton onClick={() => onRowTap(order)} sx={{ px: 3 }}>
                    <ListItemText
                      primary={name}
                      secondary={t('checkin_scanner.row_secondary', {
                        persons: order.persons,
                        time: timeLabel,
                      })}
                      secondaryTypographyProps={{ sx: { fontVariantNumeric: 'tabular-nums' } }}
                    />
                    {checkin ? (
                      <Chip
                        size="small"
                        color="success"
                        label={t('checkin_scanner.row_checked_in', {
                          time: TimeUtils.getLocalTime(checkin.created_at, account?.timezone || 'utc').format(
                            timeFormat,
                          ),
                        })}
                      />
                    ) : null}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default SearchFallbackView;
